/**
 * Comments Service - API calls for comment operations
 */

import { httpClient, HttpError } from '@/lib/http'
import type {
  CommentResponse,
  CommentListResponse,
  CommentVoteResponse,
  CreateCommentRequest,
  UpdateCommentRequest,
  VoteCommentRequest,
  RecentDiscussionsResponse,
} from '@/lib/types/comments'
import type { ApiError } from '@/lib/services/productService'

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

interface GetCommentsResult {
  success: boolean
  data?: CommentListResponse
  error?: ApiError
}

interface CommentMutationResult {
  success: boolean
  data?: CommentResponse
  error?: ApiError
}

interface VoteResult {
  success: boolean
  data?: CommentVoteResponse
  error?: ApiError
}

interface DeleteResult {
  success: boolean
  error?: ApiError
}

interface RecentDiscussionsResult {
  success: boolean
  data?: RecentDiscussionsResponse
  error?: ApiError
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Get all comments for a product (public)
 */
export async function getProductComments(productSlug: string): Promise<GetCommentsResult> {
  try {
    const response = await httpClient.get<CommentListResponse>(`/products/${productSlug}/comments`)
    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Failed to fetch comments:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось загрузить комментарии',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Create a new comment on a product (authenticated)
 */
export async function createComment(
  productSlug: string,
  content: string,
  parentId?: number | null
): Promise<CommentMutationResult> {
  try {
    const request: CreateCommentRequest = {
      content,
      parentId: parentId ?? null,
    }

    const response = await httpClient.post<CommentResponse>(
      `/products/${productSlug}/comments`,
      request as unknown as Record<string, unknown>
    )

    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Failed to create comment:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось опубликовать комментарий',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Update an existing comment (authenticated, owner only)
 */
export async function updateComment(
  commentId: number,
  content: string
): Promise<CommentMutationResult> {
  try {
    const request: UpdateCommentRequest = { content }

    const response = await httpClient.put<CommentResponse>(
      `/comments/${commentId}`,
      request as unknown as Record<string, unknown>
    )

    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Failed to update comment:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось обновить комментарий',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Delete a comment (authenticated, owner only)
 */
export async function deleteComment(commentId: number): Promise<DeleteResult> {
  try {
    await httpClient.delete(`/comments/${commentId}`)

    return {
      success: true,
    }
  } catch (error) {
    console.error('Failed to delete comment:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось удалить комментарий',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Vote on a comment (authenticated)
 * Direction: 1 = upvote, -1 = downvote, 0 = remove vote
 */
export async function voteOnComment(
  commentId: number,
  direction: 1 | -1 | 0
): Promise<VoteResult> {
  try {
    const request: VoteCommentRequest = { direction }

    const response = await httpClient.post<CommentVoteResponse>(
      `/comments/${commentId}/vote`,
      request as unknown as Record<string, unknown>
    )

    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Failed to vote on comment:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось проголосовать',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Report a comment (authenticated)
 */
export async function reportComment(commentId: number): Promise<DeleteResult> {
  try {
    await httpClient.post(`/comments/${commentId}/report`, {})

    return {
      success: true,
    }
  } catch (error) {
    console.error('Failed to report comment:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось пожаловаться на комментарий',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}

/**
 * Get recent discussions for the main page widget (public)
 */
export async function getRecentDiscussions(): Promise<RecentDiscussionsResult> {
  try {
    const response = await httpClient.get<RecentDiscussionsResponse>('/comments/recent')
    return {
      success: true,
      data: response,
    }
  } catch (error) {
    console.error('Failed to fetch recent discussions:', error)

    if (error instanceof HttpError) {
      return {
        success: false,
        error: {
          message: error.message,
          code: error.code,
        },
      }
    }

    return {
      success: false,
      error: {
        message: 'Не удалось загрузить обсуждения',
        code: 'UNKNOWN_ERROR',
      },
    }
  }
}
